<template>
  <div>
    <!-- 派遣位置 -->
    <div class="mx-4 mb-4 font-bold flex items-center red-dot">
      {{ $t(`mission.location.title`) }}
    </div>

    <!-- 星球选择 -->
    <div
      class="px-4 flex gap-4 overflow-x-auto pb-2 mb-2"
      v-if="!isWorldMission"
    >
      <div v-if="!isEdit" class="flex flex-col items-center w-12">
        <div class="avatar">
          <div class="w-12">
            <img
              :src="
                dispatchMission?.location?.worldview?.media?.url ||
                useDefaultWorldviewImg()
              "
              alt="地球"
              crossorigin="anonymous"
              class="grayscale-0 play-animation"
            />
          </div>
        </div>
        <span class="text-xs mt-1 line-clamp-1">
          {{ useName(dispatchMission?.location?.worldview?.name) }}
        </span>
      </div>

      <div
        v-else
        v-for="item in worldviewStore.worldviewList"
        :key="item.id"
        class="flex flex-col items-center w-12"
        @click="clickWorldview(item)"
      >
        <div class="avatar">
          <div :class="[activeWorldview?.id === item.id ? 'w-12' : 'w-10']">
            <img
              :src="item.media?.url || useDefaultWorldviewImg()"
              alt="地球"
              crossorigin="anonymous"
              :class="
                activeWorldview?.id === item.id
                  ? 'grayscale-0 play-animation'
                  : 'grayscale stop-animation'
              "
            />
          </div>
        </div>
        <span class="text-xs mt-1 line-clamp-1">
          {{ useName(item.name) }}
        </span>
      </div>
    </div>

    <!-- 地点选择 -->
    <VanCellGroup inset class="rounded-none">
      <VanCell v-if="!isEdit" :title="dispatchMission?.location?.name">
        <template #label v-if="isWorldMission">
          {{ dispatchMission?.location?.worldview?.description }}
        </template>
      </VanCell>

      <template v-else-if="activeWorldview">
        <VanCell>
          <template #title>
            <p>{{ $t(`mission.location.select`) }}</p>
          </template>
          <template #value>
            <button class="btn btn-outline btn-xs" @click="clickEditLocation()">
              <i class="iconify mingcute--add-fill w-[10px]"></i>
              {{ $t(`mission.location.new`) }}
            </button>
          </template>
        </VanCell>
        <template v-if="locationLoading">
          <div class="flex items-center justify-center pt-1">
            <div class="loading loading-ring"></div>
          </div>
        </template>
        <template v-else-if="activeWorldview?.locationzz?.length === 0">
          <VanEmpty :description="$t(`mission.location.no`)" :image-size="60" />
        </template>
        <template v-else>
          <VanCell
            v-for="location in activeWorldview?.locationzz"
            v-show="isEdit || location.id === dispatchMission!.location?.id"
            :key="location.id"
            :title="location.name"
            title-class="text-xs text-base-content/80"
          >
            <template #value>
              <div class="flex gap-1 justify-end">
                <button
                  :class="[
                'btn btn-outline btn-xs',
                dispatchMission!.location?.id === location.id
                  ? 'btn-primary'
                  : 'grayscale',
              ]"
                  @click="clickLocation(location)"
                >
                  <template
                    v-if="dispatchMission!.location?.id === location.id"
                  >
                    {{ $t(`Buttons.selected`) }}
                  </template>
                  <template v-else>
                    {{ $t(`Buttons.select`) }}
                  </template>
                </button>

                <template v-if="isEdit">
                  <button
                    class="btn btn-outline btn-xs"
                    @click="clickEditLocation(location)"
                  >
                    <i class="iconify mingcute--edit-2-line w-4"></i>
                  </button>

                  <button
                    class="btn btn-outline btn-xs"
                    @click="clickRemoveLocation(location)"
                  >
                    <i class="iconify mingcute--delete-line w-4"></i>
                  </button>
                </template>
              </div>
            </template>
          </VanCell>
        </template>
      </template>
    </VanCellGroup>

    <!-- Add new edit modal -->
    <VanPopup v-model:show="showLocationEditModal" round teleport="body">
      <div class="grid grid-rows-[auto_1fr_auto] overflow-hidden h-full">
        <div
          class="w-full flex items-center justify-center py-3 text-base-content border-b font-bold"
        >
          {{ $t("mission.location.newTitle", { name: activeWorldview?.name }) }}
        </div>

        <div class="overflow-y-auto">
          <VanCellGroup inset>
            <VanField
              v-model="editLocationName"
              :label="$t('game.name')"
              :placeholder="$t('mission.location.placeholder.name')"
            />
            <VanField
              v-model="editLocationDescription"
              type="textarea"
              :label="$t('trait.description')"
              :placeholder="$t('mission.location.placeholder.description')"
              :rows="4"
            />
          </VanCellGroup>
        </div>

        <div class="p-4 flex gap-2">
          <button
            class="btn btn-sm flex-1"
            @click="showLocationEditModal = false"
          >
            {{ $t("Buttons.cancel") }}
          </button>
          <button class="btn btn-sm btn-primary flex-1" @click="onSaveLocation">
            {{ $t("Buttons.save") }}
          </button>
        </div>
      </div>
    </VanPopup>
  </div>
</template>

<script setup lang="ts">
import { useWorldview } from "@/stores/worldview";
import { MateDispatch, Worldview, WorldviewLocation } from "@/types/entity";
import { useDefaultWorldviewImg, useName } from "@/use/common";
import { showConfirmDialog, showLoadingToast } from "vant";
import { onMounted, PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import { checkAndInitObjective } from "./MissionGenerate";

const props = defineProps({
  isEdit: {
    type: Boolean,
    default: false,
  },
  dispatchMission: {
    type: Object as PropType<Partial<MateDispatch>>,
  },
  isWorldMission: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const activeWorldview = ref<Worldview>();

const showLocationEditModal = ref(false);
const locationLoading = ref(false);
const editLocation = ref<WorldviewLocation>({} as WorldviewLocation);
const editLocationName = ref("");
const editLocationDescription = ref("");

const onSaveLocation = async () => {
  const loading = showLoadingToast(t(`Message.loading`));
  try {
    if (editLocation.value.id) {
      editLocation.value.name = editLocationName.value;
      editLocation.value.description = editLocationDescription.value;
      await worldviewStore.updateWorldviewLocation(
        editLocation.value.id,
        editLocation.value
      );
    } else {
      await worldviewStore.createWorldviewLocation(
        activeWorldview.value!.id,
        editLocationName.value,
        editLocationDescription.value
      );
    }
    loading.close();
    showLocationEditModal.value = false;
  } catch (error) {
    loading.close();
    console.error(error);
  }
};

const worldviewStore = useWorldview();
const clickWorldview = async (w: Worldview) => {
  activeWorldview.value = w;
};

const clickEditLocation = (location?: WorldviewLocation) => {
  editLocation.value = location || ({} as WorldviewLocation);
  editLocationName.value = editLocation.value.name || "";
  editLocationDescription.value = editLocation.value.description || "";
  showLocationEditModal.value = true;
};

const clickRemoveLocation = async (location: WorldviewLocation) => {
  try {
    await showConfirmDialog({
      message: t("Message.deleteConfirm"),
      confirmButtonText: t("Buttons.confirm"),
      cancelButtonText: t("Buttons.cancel"),
    });
  } catch (e) {
    return;
  }
  const loading = showLoadingToast(t(`Message.loading`));
  try {
    await worldviewStore.deleteWorldviewLocation(
      location.id,
      location.worldviewId
    );

    if (props.dispatchMission!.location?.id === location.id) {
      props.dispatchMission!.location = undefined;
    }
  } finally {
    loading.close();
  }
};

const clickLocation = async (location: WorldviewLocation) => {
  if (props.dispatchMission!.location?.id === location.id) return;

  const pass = await checkAndInitObjective(props.dispatchMission!);
  if (!pass) return;

  props.dispatchMission!.location = location;
};

onMounted(() => {
  if (props.isEdit) {
    // activeWorldview.value = worldviewStore.worldviewList[0];
    if (activeWorldview.value?.locationzz?.length) {
      // props.dispatchMission!.location = activeWorldview.value.locationzz[0];
    }
  } else {
    if (props.dispatchMission!.location) {
      activeWorldview.value =
        worldviewStore.worldviewMap[
          props.dispatchMission!.location!.worldviewId
        ];
    }
  }
});
</script>
